import { useEffect } from 'react';

/**
 * Apply per-page document head metadata: title, meta tags, canonical link, and
 * JSON-LD structured data.
 *
 * Replaces react-helmet-async, which silently stopped applying anything in this
 * app: the components rendered and computed the right values, but no tag ever
 * reached the document.
 *
 * This project is on React 18, which does NOT hoist `title`/`meta` on its own,
 * so these writes are what put them in the document. React 19 does hoist them
 * natively; when this project moves, most of this hook can be deleted in favour
 * of rendering the tags directly. That is a reason to own the few lines here
 * rather than carry an unmaintained dependency, not something already in play.
 *
 * Two behaviours are deliberate:
 *
 * 1. Tags are UPSERTED by selector, not appended. `public/index.html` ships
 *    static `<title>`, canonical, and Open Graph tags so a crawler that does not
 *    run JavaScript still sees something. Appending would leave two canonicals
 *    and two og:titles on the page, and two canonicals is treated as none.
 *    Updating the existing element in place keeps exactly one of each.
 *
 * 2. JSON-LD blocks are owned, so they are removed and re-added on every change.
 *    They cannot be upserted by attribute the way a meta tag can (a page may emit
 *    several, and their content is the identity), and a stale schema left behind
 *    from a previous route is worse than none.
 *
 * Tags are not reverted to the static defaults on unmount. Every route that
 * cares sets its own, so reverting would only add a flash of the wrong title
 * between pages.
 */

/** Marks the JSON-LD blocks this hook owns, so it only ever removes its own. */
const MANAGED_JSONLD_ATTR = 'data-managed-jsonld';

/**
 * Find or create a head element matching `selector`, then apply `attrs`.
 * @param {string} selector - CSS selector identifying the tag (e.g. 'meta[name="description"]').
 * @param {string} tagName - Element to create when the selector matches nothing.
 * @param {object} attrs - Attributes to set on the element.
 * @returns {void}
 */
function upsertHeadTag(selector, tagName, attrs) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(tagName);
        document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([key, value]) => {
        el.setAttribute(key, value);
    });
}

/**
 * Set a `<meta>` tag identified by `name` (the `name` attribute form).
 * @param {string} name - The meta tag's name attribute.
 * @param {string} content - The content to apply.
 * @returns {void}
 */
function setMetaByName(name, content) {
    upsertHeadTag(`meta[name="${name}"]`, 'meta', { name, content });
}

/**
 * Set a `<meta>` tag identified by `property` (the Open Graph form).
 * @param {string} property - The meta tag's property attribute.
 * @param {string} content - The content to apply.
 * @returns {void}
 */
function setMetaByProperty(property, content) {
    upsertHeadTag(`meta[property="${property}"]`, 'meta', {
        property,
        content,
    });
}

/**
 * Apply document head metadata for the current page.
 *
 * @param {object} head - The resolved head values (already defaulted by the caller).
 * @param {string} head.title - Full document title, used verbatim.
 * @param {string} head.description - Meta + Open Graph description.
 * @param {string} head.canonicalUrl - Absolute canonical URL for this page.
 * @param {string} head.image - Absolute URL of the social preview image.
 * @param {string} head.type - Open Graph type (e.g. 'website', 'article').
 * @param {boolean} head.noindex - When true, emit noindex/nofollow instead of index/follow.
 * @param {object[]} [head.structuredData] - JSON-LD objects to emit, one script each.
 * @returns {void}
 */
export default function useDocumentHead({
    title,
    description,
    canonicalUrl,
    image,
    type,
    noindex,
    structuredData = [],
}) {
    // structuredData is rebuilt by the caller on every render, so it is a new
    // array identity each time. Serialising it keeps this effect keyed on the
    // CONTENT; depending on the array itself would re-run the effect (and
    // rewrite every tag) on every render of every page.
    const structuredDataKey = JSON.stringify(structuredData);

    useEffect(() => {
        document.title = title;

        setMetaByName('title', title);
        setMetaByName('description', description);
        setMetaByName(
            'robots',
            noindex ? 'noindex, nofollow' : 'index, follow'
        );

        upsertHeadTag('link[rel="canonical"]', 'link', {
            rel: 'canonical',
            href: canonicalUrl,
        });

        setMetaByProperty('og:type', type);
        setMetaByProperty('og:url', canonicalUrl);
        setMetaByProperty('og:title', title);
        setMetaByProperty('og:description', description);
        setMetaByProperty('og:image', image);

        setMetaByName('twitter:card', 'summary_large_image');
        setMetaByName('twitter:url', canonicalUrl);
        setMetaByName('twitter:title', title);
        setMetaByName('twitter:description', description);
        setMetaByName('twitter:image', image);

        // Remove only our own blocks, so a JSON-LD script added by anything else
        // (an analytics snippet, a future server-rendered block) survives.
        document.head
            .querySelectorAll(`script[${MANAGED_JSONLD_ATTR}]`)
            .forEach((el) => el.remove());

        const parsed = JSON.parse(structuredDataKey);
        parsed.forEach((data) => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute(MANAGED_JSONLD_ATTR, 'true');
            script.textContent = JSON.stringify(data);
            document.head.appendChild(script);
        });
    }, [
        title,
        description,
        canonicalUrl,
        image,
        type,
        noindex,
        structuredDataKey,
    ]);
}
